import type { CoreFeeling } from "./feelings-pathway";

/**
 * Immediate, low-effort support offered straight after a feeling is chosen.
 *
 * The wheel names the feeling; this turns that name into something the person
 * can actually do in the next minute. Deeper reflection stays available, but
 * it is one option among several rather than the only way forward.
 */
export type SupportOption = {
  key: "ground" | "mind" | "body" | "reflect" | "listen" | "unsure";
  emoji: string;
  title: string;
  line: string;
  /** Present for options that open an existing practice. */
  to?: string;
  memberOnly?: boolean;
};

type Pick = { title: string; line: string; to: string; memberOnly?: boolean };

const MIND: Record<string, Pick> = {
  shame: {
    title: "Soften the inner critic",
    line: "Where that harsh voice can come from, and how it eases.",
    to: "/learn/inner-critic",
  },
  guilt: {
    title: "Question the thought",
    line: "Gently test a thought that feels like a fact.",
    to: "/cbt-thought-cards",
    memberOnly: true,
  },
  sad: {
    title: "Speak to yourself kindly",
    line: "A short self-compassion practice for heavy moments.",
    to: "/self-love",
    memberOnly: true,
  },
  anxious: {
    title: "Question the worry",
    line: "Gently test a thought that feels like a fact.",
    to: "/cbt-thought-cards",
    memberOnly: true,
  },
  angry: {
    title: "Name the line that was crossed",
    line: "What boundaries are, and gentle ways to set them.",
    to: "/learn/boundaries",
  },
  lonely: {
    title: "Reach towards people",
    line: "The community, at whatever distance feels right.",
    to: "/community",
  },
  rejected: {
    title: "Tend the younger part of you",
    line: "Inner-child work for moments that feel very old.",
    to: "/inner-child",
    memberOnly: true,
  },
  overwhelmed: {
    title: "Take a little weight off",
    line: "Two-minute resets for when capacity is low.",
    to: "/quick-resets",
  },
  numb: {
    title: "Let it out without words",
    line: "Creative reflection when language feels far away.",
    to: "/creative-reflection",
    memberOnly: true,
  },
  afraid: {
    title: "Build a sense of safety",
    line: "Steadying practices that come before anything else.",
    to: "/safety-stabilisation",
    memberOnly: true,
  },
  disappointed: {
    title: "Speak to yourself kindly",
    line: "A short self-compassion practice for heavy moments.",
    to: "/self-love",
    memberOnly: true,
  },
  unworthy: {
    title: "Challenge the thought",
    line: "A gentle self-worth practice for when you feel not enough.",
    to: "/self-love",
    memberOnly: true,
  },
};

const GENERIC_MIND: Pick = {
  title: "Speak to yourself kindly",
  line: "A short self-compassion practice you can borrow.",
  to: "/self-love",
  memberOnly: true,
};

const BODY_MOVE = new Set(["angry", "numb", "overwhelmed"]);
const LISTEN_MEDITATION = new Set(["sad", "lonely", "unworthy", "rejected"]);

/** Specific exercise each quick option opens, so a tap lands on the practice itself, not a whole library. */
const GROUND_FOR: Record<string, string> = {
  anxious: "5-4-3-2-1", afraid: "orienting", overwhelmed: "feet-on-ground", numb: "temperature",
  angry: "feet-on-ground", sad: "self-soothe", lonely: "self-soothe", shame: "safe-place",
  guilt: "safe-place", rejected: "self-soothe", unworthy: "body-anchors", disappointed: "sensory-grounding",
};
const MEDITATION_FOR: Record<string, string> = {
  sad: "grief-holding", lonely: "inner-child", unworthy: "self-compassion", rejected: "self-compassion",
};

/** Six immediate options, matched to the feeling and always in the same order. */
export function supportOptionsFor(feeling: CoreFeeling | null): SupportOption[] {
  const key = feeling?.key ?? "";
  const mind = MIND[key] ?? GENERIC_MIND;
  const move = BODY_MOVE.has(key);
  const meditate = LISTEN_MEDITATION.has(key);

  return [
    {
      key: "ground",
      emoji: "🌿",
      title: "Ground myself",
      line: "A short grounding practice to come back to right now.",
      to: `/grounding?open=${GROUND_FOR[key] ?? "5-4-3-2-1"}`,
    },
    {
      key: "mind",
      emoji: "💛",
      title: mind.title,
      line: mind.line,
      to: mind.to,
      ...(mind.memberOnly ? { memberOnly: true } : {}),
    },
    {
      key: "body",
      emoji: "🫁",
      title: move ? "Move and release" : "Regulate my body",
      line: move
        ? "Body-based movement to let some of it move through."
        : "Breathing with a long, slow out-breath.",
      to: move ? "/somatic" : "/breathing?open=extended-exhale",
    },
    {
      key: "reflect",
      emoji: "✍️",
      title: "Reflect on this",
      line: "Optional prompts, only if writing feels helpful today.",
    },
    {
      key: "listen",
      emoji: "🎧",
      title: "Listen and settle",
      line: meditate
        ? "A gentle guided meditation to rest with."
        : "Calming sounds you can simply rest with.",
      to: meditate ? `/meditations?open=${MEDITATION_FOR[key] ?? "calming-ns"}` : "/sounds",
    },
    {
      key: "unsure",
      emoji: "🧭",
      title: "I'm not sure — help me choose",
      line: "Answer one simple question and we'll suggest something.",
    },
  ];
}
